import type { EffectRef } from '@angular/core';
import type { CanActivateFn, GuardResult } from '@angular/router';
import { isPlatformServer, PlatformLocation } from '@angular/common';
import { effect, inject, Injector, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

import { isNonEmptyString } from '@accesso/cc-std-lib';

import { MerchantStore } from '../../feature/merchant/merchant.store';
import { StorageService } from '../../feature/storage/storage.token';
import { AuthService } from './auth.service';

export const profileCompleteGuard: CanActivateFn = () => {
	if (isPlatformServer(inject(PLATFORM_ID))) return true;

	const storage = inject(StorageService);
	const router = inject(Router);
	const auth = inject(AuthService);
	const injector = inject(Injector);
	const platform = inject(PlatformLocation);
	const merchantStore = inject(MerchantStore);

	if (
		// If storage isnt supported, they cant login as its required for accesso ID
		!storage.isSupported ||
		// If we're on the auth_callback, we want to let them through
		platform.pathname.startsWith('/auth_callback') ||
		// If the merchant doesn't support profiles, resolve
		!merchantStore.merchant()?.usesProfiles()
	)
		return true;

	return auth.initialize().then(() => {
		return new Promise((resolve) => {
			let destroyRef: EffectRef | null = null;

			const resolvePromise = (data: GuardResult) => {
				destroyRef?.destroy();
				resolve(data);
			};

			destroyRef = effect(
				() => {
					const profile = auth.profile();
					if (!auth.isAuthenticated() || !profile) return resolvePromise(true);

					const state = profile.state();
					if (state.loading) return;

					if (state.error) return resolvePromise(router.createUrlTree(['/auth_callback']));

					const { firstName, lastName } = profile.personal() ?? {};
					if (state.loaded) {
						if ([firstName, lastName].every(isNonEmptyString)) return resolvePromise(true);
					}

					return resolvePromise(router.createUrlTree(['/auth_callback']));
				},
				{ injector, manualCleanup: true }
			);
		});
	});
};
